import { ButtonSecondary, Colors, Icon, Label, Tabs, Text, TextInput } from '@amelio/ui-library';

import { COMPENSATION_TAB_BUDGET } from '../compensationStyles';
import { parseTypedNumber, useTypedNumber } from '../hooks/useTypedNumber';

import type { CompensationCycleOverviewEnvelope } from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { formatCurrency } from '@/utils/numberFormat';

interface EnvelopeTabsProps {
    envelopes: CompensationCycleOverviewEnvelope[];
    activeEnvelopeId: string | undefined;
    /** Sum of the cycle's envelope budgets, in whole dollars. */
    totalBudget: number;
    /** The cycle-wide ceiling, or `null` when none has been set. HR only. */
    cycleBudget: number | null;
    /** By how much the envelopes exceed the cycle budget, as the server computed it. */
    cycleOverrun: number | null;
    /** Whether the reader may set the cycle-wide budget (company-wide scope). */
    canSetCycleBudget: boolean;
    /** Names each envelope's manager under its tab, for a reader who sees several managers'. */
    showsOwners: boolean;
    onSelect: (envelopeId: string) => void;
    onCreate: () => void;
    onCycleBudgetChange: (amount: number | null) => void;
    isCreating: boolean;
}

const CYCLE_BUDGET_INPUT_ID = 'compensation-cycle-budget';

/** One tab per envelope of the selected cycle, the "new envelope" action, the running total and —
 * for company-wide scope — each envelope's manager and the optional cycle-wide budget the total is
 * checked against. */
export const EnvelopeTabs = ({
    envelopes,
    activeEnvelopeId,
    totalBudget,
    cycleBudget,
    cycleOverrun,
    canSetCycleBudget,
    showsOwners,
    onSelect,
    onCreate,
    onCycleBudgetChange,
    isCreating,
}: EnvelopeTabsProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabelCapitalized } = useCompanyInformation();
    const language = useSelectedAppLanguage();

    // Saving the cycle budget is a PUT, so the field only commits what the reader settled on when
    // they leave it.
    const cycleBudgetText = cycleBudget === null ? '' : String(cycleBudget);
    const [budgetText, typeBudgetText] = useTypedNumber(cycleBudgetText);

    const commitCycleBudget = () => {
        const amount = parseTypedNumber(budgetText);

        if (amount === null && budgetText.trim() !== '') {
            typeBudgetText({ raw: cycleBudgetText, nextValueText: cycleBudgetText });
            return;
        }

        if (amount !== cycleBudget) {
            onCycleBudgetChange(amount);
        }
    };

    const overrun = cycleOverrun ?? 0;

    return (
        <div className={'flex flex-col gap-[8px]'}>
            <div className={'flex gap-[12px] max-md:flex-col md:items-end md:justify-between'}>
                <div className={'flex min-w-0 items-center gap-[8px]'}>
                    <Tabs
                        variant={'underline'}
                        ariaLabel={t('compensation.tabs.navLabel')}
                        activeKey={activeEnvelopeId ?? ''}
                        onChange={onSelect}
                        items={envelopes.map((envelope) => ({
                            key: envelope.id,
                            label: (
                                <>
                                    {/* Explicit spaces: the margin between the parts is drawn, not
                                        spoken, and the tab's accessible name ran them together. */}
                                    {envelope.name}{' '}
                                    <span
                                        className={`${COMPENSATION_TAB_BUDGET} text-base-500 ml-2`}
                                    >
                                        {formatCurrency(envelope.budgetAmount, language)}
                                    </span>
                                    {showsOwners ? (
                                        <>
                                            {' '}
                                            <span
                                                className={`${COMPENSATION_TAB_BUDGET} text-base-500 block text-left`}
                                            >
                                                {envelope.owner.name
                                                    ? t('compensation.tabs.owner', {
                                                          name: envelope.owner.name,
                                                          managerLabel:
                                                              getManagerLabelCapitalized(),
                                                      })
                                                    : t('compensation.tabs.unknownOwner', {
                                                          managerLabel:
                                                              getManagerLabelCapitalized(),
                                                      })}
                                            </span>
                                        </>
                                    ) : null}
                                </>
                            ),
                            dataTestId: `compensation-envelope-tab-${envelope.id}`,
                        }))}
                        dataTestId={'compensation-envelope-tabs'}
                    />

                    <ButtonSecondary
                        type={'button'}
                        size={'sm'}
                        onClick={onCreate}
                        disabled={isCreating}
                        leftIcon={
                            <Icon
                                name={'essential-add'}
                                size={'sm'}
                                color={'inherit'}
                                ariaHidden
                            />
                        }
                        dataTestId={'compensation-new-envelope'}
                    >
                        {t('compensation.tabs.newEnvelope')}
                    </ButtonSecondary>
                </div>

                <div className={'flex flex-wrap items-end gap-[16px] pb-[12px]'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {t('compensation.tabs.totalEnvelopesLabel')}{' '}
                        <Text
                            as={'span'}
                            size={'small'}
                            weight={'semibold'}
                            color={Colors.NAVY}
                        >
                            {formatCurrency(totalBudget, language)}
                        </Text>
                    </Text>

                    {canSetCycleBudget ? (
                        <div className={'flex flex-col gap-1'}>
                            <Label
                                htmlFor={CYCLE_BUDGET_INPUT_ID}
                                color={Colors.BASE_500}
                            >
                                {t('compensation.cycleBudget.label')}
                            </Label>

                            <div className={'w-[160px]'}>
                                <TextInput
                                    id={CYCLE_BUDGET_INPUT_ID}
                                    type={'number'}
                                    size={'sm'}
                                    value={budgetText}
                                    onChange={(raw) =>
                                        typeBudgetText({ raw, nextValueText: cycleBudgetText })
                                    }
                                    onBlur={commitCycleBudget}
                                    placeholder={t('compensation.cycleBudget.placeholder')}
                                    error={overrun > 0}
                                    dataTestId={'compensation-cycle-budget-input'}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {overrun > 0 ? (
                <div
                    className={'flex justify-end'}
                    data-testid={'compensation-cycle-budget-overrun'}
                >
                    <Text
                        size={'small'}
                        color={Colors.ERROR_800}
                    >
                        {t('compensation.cycleBudget.overrun', {
                            amount: formatCurrency(overrun, language),
                        })}
                    </Text>
                </div>
            ) : null}
        </div>
    );
};
